import React from 'react';

import type { ISwitchProps } from './Switch.types';

export const Switch: React.FC<ISwitchProps> = ({
  label,
  description,
  size = 'md',
  containerClassName = '',
  labelClassName = '',
  disabled = false,
  checked = false,
  id,
  className = '',
  ...switchProps
}: ISwitchProps) => {

  // Size mapping for switch - ensuring equal padding on all sides
  const sizeMapper = {
    'sm': {
      track: 'w-9 h-5',
      thumb: 'w-3 h-3',
      translateChecked: 'translate-x-4',
      translateUnchecked: 'translate-x-0',
      text: 'text-bg-v2-sm',
      gap: 'gap-bg-v2-xs',
    },
    'md': {
      track: 'w-11 h-6',
      thumb: 'w-4 h-4',
      translateChecked: 'translate-x-5',
      translateUnchecked: 'translate-x-0',
      text: 'text-bg-v2-md',
      gap: 'gap-bg-v2-sm',
    },
    'lg': {
      track: 'w-14 h-7',
      thumb: 'w-5 h-5',
      translateChecked: 'translate-x-7',
      translateUnchecked: 'translate-x-0',
      text: 'text-bg-v2-lg',
      gap: 'gap-bg-v2-md',
    },
  };

  // Generate unique ID if not provided
  const switchId = id || `switch-${Math.random().toString(36).substr(2, 9)}`;

  const currentSize = sizeMapper[size];

  return (
    <div className={`flex flex-col ${currentSize.gap} ${containerClassName}`}>
      <div className={`flex items-center ${currentSize.gap}`}>
        <label
          htmlFor={switchId}
          className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <input
            type="checkbox"
            id={switchId}
            disabled={disabled}
            checked={checked}
            className="sr-only peer"
            {...switchProps}
          />
          <div
            className={`
              ${currentSize.track}
              relative
              flex
              items-center
              p-1
              ${checked ? 'bg-bg-brand-solid' : 'bg-bg-quaternary'}
              peer-focus-visible:outline-none
              peer-focus-visible:ring-2
              peer-focus-visible:ring-offset-2
              peer-focus-visible:ring-border-brand-solid
              peer-disabled:bg-bg-disabled
              peer-disabled:cursor-not-allowed
              rounded-bg-v2-full
              transition-colors
              duration-200
              ${className}
            `.trim().replace(/\s+/g, ' ')}
          >
            <div
              className={`
                ${currentSize.thumb}
                bg-bg-primary
                rounded-bg-v2-full
                shadow-bg-v2-sm
                transition-transform
                duration-200
                ease-in-out
                ${checked ? currentSize.translateChecked : currentSize.translateUnchecked}
              `.trim().replace(/\s+/g, ' ')}
            />
          </div>
        </label>
        
        {label && (
          <div className="flex flex-col gap-bg-v2-xxs flex-1">
            <label
              htmlFor={switchId}
              className={`
                ${currentSize.text}
                font-bg-v2-medium
                text-text-primary
                ${disabled ? 'text-text-disabled cursor-not-allowed' : 'cursor-pointer'}
                ${labelClassName}
              `.trim().replace(/\s+/g, ' ')}
            >
              {label}
            </label>
            
            {description && (
              <span className={`text-bg-v2-sm text-text-tertiary ${disabled ? 'text-text-disabled' : ''}`}>
                {description}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

